import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { ArrowUp, ArrowDown, Cpu, MemoryStick } from 'lucide-react';

/**
 * S11 — Per-dimension increase/decrease toggles.
 *
 * Four bool switches matching `RightsizingPolicy`:
 *   cpu_increase_enabled, cpu_decrease_enabled,
 *   mem_increase_enabled, mem_decrease_enabled.
 *
 * Default: all four ON (legacy behavior). Common operator presets:
 *   "Cost-only": both decreases ON, both increases OFF.
 *   "Resilience-only": both increases ON, both decreases OFF.
 *   "Never touch memory": mem.increase=OFF, mem.decrease=OFF.
 */

const PRESETS = [
  { name: 'all',         label: 'All directions',
    cpu_increase_enabled: true,  cpu_decrease_enabled: true,
    mem_increase_enabled: true,  mem_decrease_enabled: true },
  { name: 'cost_only',   label: 'Cost-only (cuts only)',
    cpu_increase_enabled: false, cpu_decrease_enabled: true,
    mem_increase_enabled: false, mem_decrease_enabled: true },
  { name: 'safe',        label: 'Resilience-only (raises only)',
    cpu_increase_enabled: true,  cpu_decrease_enabled: false,
    mem_increase_enabled: true,  mem_decrease_enabled: false },
  { name: 'frozen_mem',  label: 'Frozen memory',
    cpu_increase_enabled: true,  cpu_decrease_enabled: true,
    mem_increase_enabled: false, mem_decrease_enabled: false },
];

export default function DimensionTogglesPanel({ clusterId, policyId, onSaved }) {
  const [state, setState] = useState({
    cpu_increase_enabled: true,
    cpu_decrease_enabled: true,
    mem_increase_enabled: true,
    mem_decrease_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clusterId || !policyId) return;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/v1/clusters/${clusterId}/rightsizing/policy/${policyId}`,
        );
        const p = r.data?.policy || r.data || {};
        setState({
          cpu_increase_enabled: p.cpu_increase_enabled !== false,
          cpu_decrease_enabled: p.cpu_decrease_enabled !== false,
          mem_increase_enabled: p.mem_increase_enabled !== false,
          mem_decrease_enabled: p.mem_decrease_enabled !== false,
        });
      } catch (err) {
        setError(err?.response?.data?.detail || String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [clusterId, policyId]);

  const toggle = (k) => setState({ ...state, [k]: !state[k] });
  const applyPreset = (p) => setState({
    cpu_increase_enabled: p.cpu_increase_enabled,
    cpu_decrease_enabled: p.cpu_decrease_enabled,
    mem_increase_enabled: p.mem_increase_enabled,
    mem_decrease_enabled: p.mem_decrease_enabled,
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.patch(
        `${API}/v1/clusters/${clusterId}/rightsizing/policy/${policyId}`,
        state,
      );
      if (onSaved) onSaved(state);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setSaving(false);
    }
  };

  const matchPreset = PRESETS.find(p => (
    p.cpu_increase_enabled === state.cpu_increase_enabled
    && p.cpu_decrease_enabled === state.cpu_decrease_enabled
    && p.mem_increase_enabled === state.mem_increase_enabled
    && p.mem_decrease_enabled === state.mem_decrease_enabled
  ));

  if (loading) {
    return <Card><CardContent>Loading dimension toggles…</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Direction Controls</CardTitle>
        <div className="text-xs text-gray-500">
          Allow rightsizing in specific directions only. Useful for "trim CPU
          but never touch memory" or "raise memory if needed, never cut".
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.name}
              variant={matchPreset?.name === p.name ? 'default' : 'outline'}
              size="sm"
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mt-4">
          <ToggleRow
            icon={<Cpu className="w-4 h-4 text-blue-600" />}
            arrow={<ArrowUp className="w-4 h-4 text-green-600" />}
            label="CPU increase"
            description="Allow raising CPU requests (perf safety)"
            checked={state.cpu_increase_enabled}
            onChange={() => toggle('cpu_increase_enabled')}
          />
          <ToggleRow
            icon={<Cpu className="w-4 h-4 text-blue-600" />}
            arrow={<ArrowDown className="w-4 h-4 text-orange-600" />}
            label="CPU decrease"
            description="Allow lowering CPU requests (cost savings)"
            checked={state.cpu_decrease_enabled}
            onChange={() => toggle('cpu_decrease_enabled')}
          />
          <ToggleRow
            icon={<MemoryStick className="w-4 h-4 text-purple-600" />}
            arrow={<ArrowUp className="w-4 h-4 text-green-600" />}
            label="Memory increase"
            description="Allow raising memory limits (OOM safety)"
            checked={state.mem_increase_enabled}
            onChange={() => toggle('mem_increase_enabled')}
          />
          <ToggleRow
            icon={<MemoryStick className="w-4 h-4 text-purple-600" />}
            arrow={<ArrowDown className="w-4 h-4 text-orange-600" />}
            label="Memory decrease"
            description="Allow lowering memory limits (cost savings)"
            checked={state.mem_decrease_enabled}
            onChange={() => toggle('mem_decrease_enabled')}
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ToggleRow({ icon, arrow, label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between border rounded p-3 bg-white">
      <div className="flex items-center gap-2">
        {icon}{arrow}
        <div>
          <div className="font-medium text-sm">{label}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
